/* eslint-disable no-loop-func */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable no-shadow */
/* eslint-disable @typescript-eslint/restrict-template-expressions */
/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable consistent-return */
/* eslint-disable @typescript-eslint/no-unnecessary-type-assertion */

import {
  Announcements,
  DndContext,
  DragEndEvent,
  DragMoveEvent,
  DragOverEvent,
  DragOverlay,
  DragStartEvent,
  DropAnimation,
  KeyboardSensor,
  MeasuringStrategy,
  Modifier,
  PointerSensor,
  UniqueIdentifier,
  closestCenter,
  defaultDropAnimation,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { CSS } from '@dnd-kit/utilities';
import { useTranslation } from 'react-i18next';
import { PsTreeItem } from '../../../../../../api/model';
import ConfirmModal from '../../../../../modules/ConfirmModal';
import { SortableTreeItem } from './components';
import { sortableTreeKeyboardCoordinates } from './keyboardCoordinates';
import type { FlattenedItem, SensorContext, TreeItem, TreeItems } from './types';
import {
  buildTree,
  flattenTree,
  getChildCount,
  getProjection,
  removeChildrenOf,
  removeItem,
  setProperty,
} from './utilities';

// ドラッグ & ドロップ。
// https://github.com/clauderic/dnd-kit/tree/master/stories/3%20-%20Examples/Tree
// を基に実装する。

const measuring = {
  droppable: {
    strategy: MeasuringStrategy.Always,
  },
};

const dropAnimationConfig: DropAnimation = {
  keyframes({ transform }) {
    return [
      { opacity: 1, transform: CSS.Transform.toString(transform.initial) },
      {
        opacity: 0,
        transform: CSS.Transform.toString({
          ...transform.final,
          x: transform.final.x + 5,
          y: transform.final.y + 5,
        }),
      },
    ];
  },
  easing: 'ease-out',
  sideEffects({ active }) {
    active.node.animate([{ opacity: 0 }, { opacity: 1 }], {
      duration: defaultDropAnimation.duration,
      easing: defaultDropAnimation.easing,
    });
  },
};

interface Props {
  /** 初期値として渡される TreeItems。 */
  defaultItems: TreeItems;
  /** 言語 ID。これに応じて TreeItem.psTreeItem の内容を表示する。 */
  languageId: number;
  /** 編集対象が L1 PS だった場合はその PS ID。そうでなければ undefined。「章 PS 編集画面ではその章 PS 配下の PS のみ移動可能」の制限のために使用する。 */
  targetL1PsId: number | undefined;
  /** ドロップ時など、items が変更された時に呼ばれる関数。引数は変更された items。 */
  onItemsChange: (treeItems: TreeItems) => void;
  collapsible?: boolean;
  indicator?: boolean;
  removable?: boolean;
  indentationWidth?: number;
}

export function SortableTree({
  defaultItems,
  languageId,
  targetL1PsId,
  onItemsChange,
  collapsible,
  indicator = false,
  removable = false,
  indentationWidth = 50,
}: Props) {
  // i18n 用。
  const {
    t,
    // i18n,
  } = useTranslation();

  const [items, setItems] = useState<TreeItems>(defaultItems);

  // console.log('items before render:', items);

  /** 所属章とは別の章 PS 配下に移動した場合は、確認ダイアログを表示する。キャンセルの場合に元に戻すために保存しておく。 */
  const [previousItems, setPreviousItems] = useState<TreeItems>([]);
  /** 確認ダイアログを表示するか否か。 */
  const [doShowConfirmModal, setDoShowConfirmModal] = useState<boolean>(false);
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
  const [overId, setOverId] = useState<UniqueIdentifier | null>(null);
  const [offsetLeft, setOffsetLeft] = useState(0);
  const [currentPosition, setCurrentPosition] = useState<{
    parentId: UniqueIdentifier | null;
    overId: UniqueIdentifier;
  } | null>(null);

  const flattenedItems = useMemo(() => {
    const flattenedTree = flattenTree(items);
    const collapsedItems = flattenedTree.reduce<string[]>(
      (acc, { children, collapsed, id }) =>
        ((collapsed && children.length) ? [...acc, id] : acc) as string[],
      []
    );

    return removeChildrenOf(
      flattenedTree,
      activeId ? [activeId, ...collapsedItems] : collapsedItems
    );
  }, [activeId, items]);

  const projected = (
    (activeId) && (overId)
  ) ?
    getProjection(
      flattenedItems,
      activeId,
      overId,
      offsetLeft,
      indentationWidth
    )
    :
    null;

  const sensorContext: SensorContext = useRef({
    items: flattenedItems,
    offset: offsetLeft,
  });

  const [coordinateGetter] = useState(() =>
    sortableTreeKeyboardCoordinates(sensorContext, indicator, indentationWidth)
  );

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter,
    })
  );

  const sortedIds = useMemo(() => flattenedItems.map(({ id }) => id), [
    flattenedItems,
  ]);

  const activeItem = activeId
    ? flattenedItems.find(({ id }) => id === activeId)
    : null;

  useEffect(() => {
    sensorContext.current = {
      items: flattenedItems,
      offset: offsetLeft,
    };
  }, [flattenedItems, offsetLeft]);

  const announcements: Announcements = {
    onDragStart({ active }) {
      return `Picked up ${active.id}.`;
    },
    onDragMove({ active, over }) {
      return getMovementAnnouncement('onDragMove', active.id, over?.id);
    },
    onDragOver({ active, over }) {
      return getMovementAnnouncement('onDragOver', active.id, over?.id);
    },
    onDragEnd({ active, over }) {
      return getMovementAnnouncement('onDragEnd', active.id, over?.id);
    },
    onDragCancel({ active }) {
      return `Moving was cancelled. ${active.id} was dropped in its original position.`;
    },
  };

  return (
    <>
      <DndContext
        accessibility={{ announcements }}
        sensors={sensors}
        collisionDetection={closestCenter}
        measuring={measuring}
        onDragStart={handleDragStart}
        onDragMove={handleDragMove}
        onDragOver={handleDragOver}
        onDragEnd={
          (dragEndEvent: DragEndEvent) => {
            handleDragEnd(dragEndEvent, targetL1PsId);
          }
        }
        onDragCancel={handleDragCancel}
      >
        <SortableContext items={sortedIds} strategy={verticalListSortingStrategy}>
          {
            flattenedItems.map(
              ({ id, children, collapsed, depth, psTreeItem, isTarget, l1PsId, }) =>
              (
                <SortableTreeItem
                  key={id}
                  id={id}
                  // value={id.toString()}
                  psTreeItem={psTreeItem as PsTreeItem}
                  languageId={languageId}
                  disabled={
                    // ルート PS は移動不可。
                    (Number(psTreeItem?.type) === 0)
                    ||
                    // 「章 PS 編集画面ではその章 PS 配下の PS のみ移動可能」の制限。
                    (Number.isInteger(targetL1PsId) && (targetL1PsId !== l1PsId))
                  }
                  isTarget={isTarget}
                  depth={id === activeId && projected ? projected.depth : depth}
                  indentationWidth={indentationWidth}
                  indicator={indicator}
                  collapsed={Boolean(collapsed && children.length)}
                  onCollapse={
                    (collapsible && children.length) ?
                      () => handleCollapse(id)
                      :
                      undefined
                  }
                  onRemove={removable ? () => handleRemove(id) : undefined}
                />
              )
            )
          }
          {
            createPortal(
              <DragOverlay
                dropAnimation={dropAnimationConfig}
                modifiers={indicator ? [adjustTranslate] : undefined}
              >
                {
                  (
                    (activeId)
                    &&
                    (activeItem)
                  )
                  &&
                  (
                    <SortableTreeItem
                      id={activeId}
                      depth={activeItem.depth}
                      clone
                      childCount={getChildCount(items, activeId) + 1}
                      // value={activeId.toString()}
                      psTreeItem={activeItem.psTreeItem as PsTreeItem}
                      languageId={languageId}
                      //* MEMO: ↓ こっちに制限は不要か。
                      // disabled={}
                      indentationWidth={indentationWidth}
                    />
                  )
                }
              </DragOverlay>,
              document.body
            )
          }
        </SortableContext>
      </DndContext>

      {
        // 確認ダイアログ表示。
        (doShowConfirmModal)
        &&
        (
          <ConfirmModal
            onClickOk={
              () => {
                // 新しいツリーで OK ということで、適用された新しいツリーを渡す。
                onItemsChange(items);
                // モーダルを閉じる。
                setDoShowConfirmModal(false);
              }
            }
            onClickCancel={
              () => {
                // 元のツリーに戻す。
                setItems(previousItems);
                // モーダルを閉じる。
                setDoShowConfirmModal(false);
              }
            }
          >
            <div>
              {t('別の章PSの配下に移動させますか？')}
            </div>
          </ConfirmModal>
        )
      }
    </>
  );

  function handleDragStart({ active: { id: activeId } }: DragStartEvent) {
    setActiveId(activeId);
    setOverId(activeId);

    const activeItem = flattenedItems.find(({ id }) => id === activeId);

    if (activeItem) {
      setCurrentPosition({
        parentId: activeItem.parentId,
        overId: activeId,
      });
    }

    document.body.style.setProperty('cursor', 'grabbing');
  }

  function handleDragMove({ delta }: DragMoveEvent) {
    setOffsetLeft(delta.x);
  }

  function handleDragOver({ over }: DragOverEvent) {
    setOverId(over?.id ?? null);
  }

  /** 新しいツリーの状態。問題無しか、制限に引っかかっているか、確認が必要かを表す。 */
  type TreeState = 'OK' | 'NG' | 'Confirm';

  function handleDragEnd({ active, over }: DragEndEvent, targetL1PsId: number | undefined) {
    resetState();

    if (projected && over) {
      const { depth, parentId } = projected;
      const clonedItems: FlattenedItem[] = JSON.parse(
        JSON.stringify(flattenTree(items))
      );
      const overIndex = clonedItems.findIndex(({ id }) => id === over.id);
      const activeIndex = clonedItems.findIndex(({ id }) => id === active.id);
      // const overTreeItem = clonedItems[overIndex];
      const activeTreeItem = clonedItems[activeIndex];

      // 移動した PS に modify_type = 2 (自身移動) を設定
      if (activeTreeItem.psTreeItem) activeTreeItem.psTreeItem.modify_type = 2;

      clonedItems[activeIndex] = { ...activeTreeItem, depth, parentId };

      const sortedItems = arrayMove(clonedItems, activeIndex, overIndex);
      const newItems = buildTree(sortedItems);

      if (newItems.length > 1) {
        // ルートが 2 つ以上できてしまった場合。
        // 変更を適用しない。
        return;
      }

      switch (
      checkNewTree(
        // 1 つも無ければドラッグもできないので、newItems.length チェックは不要。
        newItems[0],
        0,
        targetL1PsId,
        undefined
      )
      ) {
        case 'OK':
          setItems(newItems);
          // items が変更されたので呼び出し。
          onItemsChange(newItems);
          break;

        case 'Confirm':
          // 復元するために、変更 (ドラッグ & ドロップ) 前のツリーを保存しておく。
          setPreviousItems(items);
          // 一旦新しいツリーを適用する。
          setItems(newItems);
          // ダイアログ表示。
          setDoShowConfirmModal(true);
          break;

        case 'NG':
          // 変更を適用しない。
          break;

        default:
          break;
      }
    }
  }

  /** 新しいツリーをチェックして、制限に違反しているものがあれば 'NG' を返し、問題なければ 'OK' を返し、確認が必要な場合は 'Confirm' を返す関数。ついでにツリーのデータも一部変更する。 */
  function checkNewTree(
    // チェック対象の TreeItem。
    treeItem: TreeItem,
    // TreeItem.psTreeItem の新しいレベル。
    level: number,
    // 編集対象が章 (L1) PS だった場合はその PS ID。そうでなければ undefined。「章 PS 編集画面ではその章 PS 配下の PS のみ移動可能」の制限のために使用する。
    targetL1PsId: number | undefined,
    // 新しいツリーにおいて、現在のチェック対象の TreeItem.psTreeItem の先祖の章 PS の PS ID。
    l1PsId: number | undefined
  ): TreeState {
    // console.log('checkNewTree() treeItem:', treeItem);

    let doConfirm = false;

    if (treeItem.l1PsId !== l1PsId) {
      // 本来の先祖の章 PS と違う章 PS の配下になっていた場合。

      // 章 PS 編集画面ではその章 PS 配下の PS のみ移動可能。
      if (Number.isInteger(targetL1PsId)) {
        // 編集対象が章 PS の場合。
        // 章を跨いだ移動は NG。
        return 'NG';
      }

      // 新しい章 PS 配下とする。
      // eslint-disable-next-line no-param-reassign
      treeItem.l1PsId = l1PsId;

      // 所属章とは別の章 PS 配下に移動した場合は、確認ダイアログを表示する。
      doConfirm = true;
    }

    // level: コンテンツ PS のレベルは親タイトル PS と同一となる。
    // 例えば、L2 のタイトル PS の子にコンテンツ PS とタイトル PS が居る場合、コンテンツ PS は 2 (L2)、タイトル PS は 3 (L3) となる。
    const newLevel = (Number(treeItem.psTreeItem?.type) === 3) ? (level - 1) : level;

    // console.log('checkNewTree() newLevel:', newLevel);


    // 移動の結果、レベル番号が 5 より大きくなってしまう PS が出来てしまう場合は移動不可。
    if (newLevel > 5) {
      return 'NG';
    }

    // 章 PS (L1) は並び順のみ変更可能。
    if (Number(treeItem.psTreeItem?.type) === 1) {
      if (newLevel !== 1) {
        // level が 1 以外になるのは不可。
        return 'NG';
      }
    }

    // L2 以下は並び順と親の変更可能。但しルート PS 直下への移動は不可。
    if (Number(treeItem.psTreeItem?.level) > 1) {
      // 元々の level が 2 以上の場合。
      if (newLevel < ((Number(treeItem.psTreeItem?.type) === 3) ? 1 : 2)) {
        // コンテンツ PS の level は 1 になってもよい (章 PS 直下)。
        // タイトル PS の level は 2 より小さくなってはいけない。
        return 'NG';
      }
    }

    if (treeItem.psTreeItem) {
      // レベルを更新。
      // eslint-disable-next-line no-param-reassign
      treeItem.psTreeItem.level = newLevel;
    }

    // eslint-disable-next-line no-plusplus
    for (let index = 0; index < treeItem.children.length; index++) {
      // タイトル PS の直下には移動可能。
      // コンテンツ PS の下には移動不可。
      // つまり、コンテンツ PS に子供がいる時点で NG。
      if (Number(treeItem.psTreeItem?.type) === 3) {
        return 'NG';
      }

      const childTreeItem = treeItem.children[index];

      switch (
      checkNewTree(
        childTreeItem,
        level + 1,
        targetL1PsId,
        // 親が L1 PS なら親の PS ID、そうでなければ親から引き継いだものを渡していく。
        (Number(treeItem.psTreeItem?.type) === 1) ?
          Number(treeItem.psTreeItem?.ps_id) : l1PsId,
      )
      ) {
        case 'NG':
          // 子孫が 1 つでも NG なら NG。
          return 'NG';

        case 'Confirm':
          //* MEMO: ドラッグ & ドロップできるのは一度に 1 つなので、return 'Confirm'; でもいいが。
          // 子孫のどれか 1 つでも Confirm が必要なら Confirm。
          doConfirm = true;
          break;

        default:
          break;
      }
    }

    return (doConfirm) ? 'Confirm' : 'OK';
  }

  function handleDragCancel() {
    resetState();
  }

  function resetState() {
    setOverId(null);
    setActiveId(null);
    setOffsetLeft(0);
    setCurrentPosition(null);

    document.body.style.setProperty('cursor', '');
  }

  function handleRemove(id: UniqueIdentifier) {
    setItems((items) => removeItem(items, id));
    // MEMO: 削除はツリーでは対応しないので不要だが、もしツリーで削除できるようにするなら、onItemsChange を呼び出す。
    // items が変更されたので呼び出し。
    // onItemsChange(newItems);
  }

  function handleCollapse(id: UniqueIdentifier) {
    setItems(
      (previousItems: TreeItems) => setProperty(JSON.parse(JSON.stringify(previousItems)), id, 'collapsed', (value) => !value)
    );
  }

  function getMovementAnnouncement(
    eventName: string,
    activeId: UniqueIdentifier,
    overId?: UniqueIdentifier
  ) {
    if (overId && projected) {
      if (eventName !== 'onDragEnd') {
        if (
          currentPosition &&
          projected.parentId === currentPosition.parentId &&
          overId === currentPosition.overId
        ) {
          return;
        }
        setCurrentPosition({
          parentId: projected.parentId,
          overId,
        });

      }

      const clonedItems: FlattenedItem[] = JSON.parse(
        JSON.stringify(flattenTree(items))
      );
      const overIndex = clonedItems.findIndex(({ id }) => id === overId);
      const activeIndex = clonedItems.findIndex(({ id }) => id === activeId);
      const sortedItems = arrayMove(clonedItems, activeIndex, overIndex);

      const previousItem = sortedItems[overIndex - 1];

      let announcement;
      const movedVerb = eventName === 'onDragEnd' ? 'dropped' : 'moved';
      const nestedVerb = eventName === 'onDragEnd' ? 'dropped' : 'nested';

      if (!previousItem) {
        const nextItem = sortedItems[overIndex + 1];
        announcement = `${activeId} was ${movedVerb} before ${nextItem.id}.`;
      } else if (projected.depth > previousItem.depth) {
        announcement = `${activeId} was ${nestedVerb} under ${previousItem.id}.`;
      } else {
        let previousSibling: FlattenedItem | undefined = previousItem;
        while (previousSibling && projected.depth < previousSibling.depth) {
          // const { parentId } = previousSibling.parentId;
          previousSibling = sortedItems.find(({ id }) => id === (previousSibling as FlattenedItem).parentId);
        }

        if (previousSibling) {
          announcement = `${activeId} was ${movedVerb} after ${previousSibling.id}.`;
        }
      }

      return announcement;
    }


  }
}


SortableTree.defaultProps = {
  collapsible: undefined,
  // defaultItems: undefined,
  indentationWidth: undefined,
  indicator: undefined,
  removable: undefined,
};

const adjustTranslate: Modifier = ({ transform }) => ({
  ...transform,
  y: transform.y - 25,
});